import { Environment, subdomainEnvironment } from './environments';

const getLaunchDarklyClientSideId = (environment: Environment): string => {
  switch (environment) {
    case 'prod':
      return '652f17e244cb56124fa8cf96';
    case 'dev':
      return '65c66efbfc74a80ff1860f04';
    default:
      return '652f17e244cb56124fa8cf95';
  }
};

// hire-go is a single multi-tenant app, so its origin is keyed off the
// environment (not the per-tenant domain). Only `prod` points at the live
// app; every non-prod environment shares the staging deployment. The sandbox
// tenant overrides this via the REACT_APP_HIRE_GO_BASE_URL env var.
export const getHireGoOrigin = (environment: Environment): string =>
  environment === 'prod'
    ? 'https://go.fountain.com'
    : 'https://staging.tryfountain.com';

export interface DomainMeta {
  environment: Environment;
  monolithOrigin: string;
  tenant: string;
  launchDarklyClientSideId: string;
}

export const inferDomainMeta = (hostname: string): DomainMeta => {
  if (hostname === 'app.fountain.com') {
    return {
      environment: 'prod',
      monolithOrigin: 'https://web.fountain.com',
      tenant: 'fountain',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('prod'),
    };
  }

  if (hostname === 'app.uat-01.fountain.com') {
    return {
      environment: 'stage',
      monolithOrigin: 'https://uat-01.fountain.com',
      tenant: 'uat-01',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('stage'),
    };
  }

  if (
    hostname.startsWith('app.') &&
    (hostname.endsWith('.fountain.com') ||
      hostname.endsWith('.tryfountain.com'))
  ) {
    const environment = subdomainEnvironment(hostname);
    return {
      environment,
      monolithOrigin: `https://${hostname.slice(4)}`,
      tenant: hostname.slice(4).split('.')[0],
      launchDarklyClientSideId: getLaunchDarklyClientSideId(environment),
    };
  }

  if (hostname.endsWith('.chromatic.com')) {
    return {
      environment: 'stage',
      monolithOrigin: 'http://localhost:3000',
      tenant: 'storybook',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('stage'),
    };
  }

  if (hostname === 'localhost' && process.env.DEV_SERVER_URL) {
    return {
      environment: 'dev',
      monolithOrigin: process.env.DEV_SERVER_URL,
      tenant: 'local',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('dev'),
    };
  }

  if (hostname === 'localhost' && process.env.NODE_ENV === 'development') {
    return {
      environment: 'dev',
      monolithOrigin: 'http://localhost:3000',
      tenant: 'local',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('dev'),
    };
  }

  // TODO: Merge the previous and following if statements and use ENV variables.
  if (hostname === 'localhost' && process.env.NODE_ENV === 'test') {
    return {
      environment: 'dev',
      monolithOrigin: 'http://localhost:3023',
      tenant: 'local',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('dev'),
    };
  }

  if (hostname.endsWith('--fountain.r1.rwx.run')) {
    // RWX dev sandbox (fountain org only — rwx.run is a shared multi-tenant domain).
    // recruiter-ui-{slug}-- or applicant-ui-{slug}-- → hire-{slug}--
    //
    // RWX also serves each task at a second, canonical task-cache-key-pinned
    // hostname with no slug in it (visual QA uses these — see
    // .rwx/sandbox-dev.yml — to guarantee it tests the exact commit a run
    // built, not whatever a same-slug redeploy currently answers under the
    // friendly hostname). The regex below can't derive a monolith hostname
    // from an opaque hash, so without this branch it silently no-ops and
    // points recruiter-ui at itself — use the build-time override
    // .rwx/sandbox-dev.yml already bakes in for exactly this case instead.
    if (
      !/^(?:recruiter-ui|applicant-ui)-/.test(hostname) &&
      process.env.REACT_APP_MONOLITH_BASE_URL
    ) {
      return {
        environment: 'dev',
        monolithOrigin: process.env.REACT_APP_MONOLITH_BASE_URL,
        tenant: 'rwx-sandbox',
        launchDarklyClientSideId: getLaunchDarklyClientSideId('dev'),
      };
    }
    const monolithHostname = hostname.replace(/^(?:recruiter-ui|applicant-ui)-/, 'hire-');
    return {
      environment: 'dev',
      monolithOrigin: `https://${monolithHostname}`,
      tenant: 'rwx-sandbox',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('dev'),
    };
  }

  if (hostname.endsWith('.modal.host')) {
    // Open-Inspect / Modal sandbox tunnel (e.g. ta-<id>-8991-<rand>.w.modal.host). recruiter-ui is
    // served through an nginx reverse proxy that routes /api_self_serve, /users, /oauth and /front
    // to the monolith on the SAME origin, so use a same-origin (empty) monolith origin.
    return {
      environment: 'dev',
      monolithOrigin: '',
      tenant: 'modal-sandbox',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('dev'),
    };
  }

  throw new Error('Unable to map hostname to a known domain');
};

export const domainMeta = inferDomainMeta(window.location.hostname);
